/**
 * Context-aware static-API bridge (Sprint 94, finding 94.3).
 *
 * WHY THIS EXISTS
 * ---------------
 * Under React 19 + AntD 6, AntD's STATIC imperative methods imported directly
 * from 'antd' — `message.*`, `notification.*`, and `Modal.confirm/info/...` —
 * render into a detached root with NO React ancestry. They therefore cannot
 * consume the `<ConfigProvider>` theme (or any React context) and AntD logs:
 *
 *   "[antd: message] Static function can not consume context like dynamic
 *    theme. Please use 'App' component instead."
 *
 * AntD's supported fix is its `<App>` holder (`antd`'s `App.useApp()`), which
 * exposes `message` / `notification` / `modal` instances that ARE wired to the
 * surrounding ConfigProvider context.
 *
 * HOW THIS BRIDGE WORKS
 * ---------------------
 * `<StaticApiBridge/>` is rendered exactly once inside the app's `<AntdApp>`
 * holder (see `App.tsx`). On mount it grabs the context-aware instances via
 * `App.useApp()` and stashes them in a module-level holder. Any non-component
 * code (helpers, stores, axios interceptors, etc.) can then import `staticApi`
 * and call e.g. `staticApi.message.success(...)` to get theme-aware surfaces
 * WITHOUT needing to be inside the React tree.
 *
 * SCOPE / FOLLOW-UP
 * -----------------
 * This module only PROVIDES the bridge. The ~91 existing call sites that still
 * use the raw static imports from 'antd' are intentionally NOT migrated here
 * (out of scope for 94.3). Call-site migration to `staticApi.*` is a tracked
 * follow-up. New / migrated code should import from this file instead of 'antd'.
 *
 * Before `<StaticApiBridge/>` has mounted (or in non-browser/test contexts
 * where it never mounts) the holder falls back to AntD's raw static methods so
 * calls never throw — they simply skip the dynamic theme, exactly as today.
 */
import { useEffect } from 'react';
import { App as AntdApp, message as staticMessage, notification as staticNotification, Modal as StaticModal } from 'antd';

// Derive the API shape straight from AntD's `App.useApp()` return type so we
// never depend on brittle internal subpath type imports (those move between
// AntD minor versions). This yields { message, notification, modal }.
type StaticApi = ReturnType<typeof AntdApp.useApp>;

// Fallback so imports never crash before the bridge mounts. The raw static
// methods satisfy the same surface (success/error/open/confirm/...); they just
// don't consume the dynamic theme. `Modal` exposes confirm/info/... statically,
// which covers the `modal` HookAPI shape used by callers.
const fallback: StaticApi = {
  message: staticMessage as unknown as StaticApi['message'],
  notification: staticNotification as unknown as StaticApi['notification'],
  modal: StaticModal as unknown as StaticApi['modal'],
};

// Module-level mutable holder, swapped to the context-aware instances once the
// bridge mounts.
const holder: { current: StaticApi } = { current: fallback };

/**
 * The context-aware AntD imperative API. Import this and call
 * `staticApi.message.success(...)` / `staticApi.notification.open(...)` /
 * `staticApi.modal.confirm(...)` from anywhere (component or not).
 */
export const staticApi: StaticApi = {
  get message() {
    return holder.current.message;
  },
  get notification() {
    return holder.current.notification;
  },
  get modal() {
    return holder.current.modal;
  },
};

/**
 * Render ONCE inside AntD's `<App>` holder. Captures the context-aware
 * message/notification/modal instances into the module holder so `staticApi`
 * resolves to them. Renders nothing.
 */
export function StaticApiBridge(): null {
  const { message, notification, modal } = AntdApp.useApp();

  useEffect(() => {
    holder.current = { message, notification, modal };
    return () => {
      // On unmount, fall back to the raw static methods so late calls don't
      // hit stale, torn-down instances.
      holder.current = fallback;
    };
  }, [message, notification, modal]);

  return null;
}

export default staticApi;
